import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { forexApi, type ModelInvocationLog } from "@/lib/forex-api";

const statusConfig: Record<string, { cls: string; label: string }> = {
  success: { cls: "bg-emerald-500/10 text-emerald-500", label: "成功" },
  fallback: { cls: "bg-amber-500/10 text-amber-500", label: "降级" },
  error: { cls: "bg-red-500/10 text-red-500", label: "错误" },
};

function formatTime(timestamp: number) {
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 19);
}

export default function ModelLogsTab() {
  const [logs, setLogs] = useState<ModelInvocationLog[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    forexApi
      .listModelInvocationLogs(statusFilter ? { status: statusFilter } : undefined)
      .then((result) => setLogs(result.items))
      .catch(() => setError("模型调用日志接口不可用，请稍后重试。"))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return logs;
    return logs.filter((log) => [log.agent_name, log.provider, log.model_id, log.request_type, log.role_key].some((value) => value.toLowerCase().includes(keyword)));
  }, [logs, search]);

  const successfulLogs = logs.filter((log) => log.latency_ms > 0);
  const totalTokens = logs.reduce((sum, log) => sum + log.total_tokens, 0);
  const totalCost = logs.reduce((sum, log) => sum + log.cost, 0);
  const avgLatency = successfulLogs.length ? Math.round(successfulLogs.reduce((sum, log) => sum + log.latency_ms, 0) / successfulLogs.length) : 0;
  const errorRate = logs.length ? ((logs.filter((log) => log.status === "error").length / logs.length) * 100).toFixed(1) : "0.0";

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">模型调用日志</h2>
        <p className="text-xs text-text-muted mt-0.5">AI 决策角色调用原子模型的完整记录：Token、延迟、成本、状态</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">调用数</p><p className="text-sm font-semibold font-mono mt-0.5">{logs.length.toLocaleString()}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">Token</p><p className="text-sm font-semibold font-mono mt-0.5">{totalTokens.toLocaleString()}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">平均延迟</p><p className="text-sm font-semibold font-mono mt-0.5">{avgLatency}ms</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">成本</p><p className="text-sm font-semibold font-mono mt-0.5">{totalCost.toFixed(4)}</p></div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[11px] text-text-muted">错误率</p><p className="text-sm font-semibold font-mono mt-0.5 text-red-500">{errorRate}%</p></div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm" />
          <input type="text" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索智能体/provider/model..." className="h-9 w-[260px] pl-9 pr-3 rounded-lg bg-surface text-sm text-text-primary placeholder:text-text-muted border border-surface-border focus:border-brand/50 focus:outline-none" />
        </div>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="h-9 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none cursor-pointer">
          <option value="">全部</option>
          <option value="success">成功</option>
          <option value="fallback">降级</option>
          <option value="error">错误</option>
        </select>
        <span className="text-xs text-text-muted ml-auto">共 {filtered.length} 条</span>
      </div>

      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载中...</div>}
      {!loading && error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      {!loading && !error && (
        <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1300px]">
              <thead>
                <tr className="border-b border-surface-border bg-surface/50">
                  <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">时间</th>
                  <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">智能体</th>
                  <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">角色</th>
                  <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">Provider/Model</th>
                  <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">请求类型</th>
                  <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px]">Prompt</th>
                  <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px]">Completion</th>
                  <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px]">总计</th>
                  <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px]">延迟</th>
                  <th className="text-right px-4 py-2.5 text-text-secondary font-medium text-[11px]">成本</th>
                  <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">状态</th>
                  <th className="text-center px-4 py-2.5 text-text-secondary font-medium text-[11px]">详情</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((log) => {
                  const st = statusConfig[log.status] || { cls: "bg-surface text-text-secondary", label: log.status };
                  return (
                    <tr key={log.log_id} className={`border-b border-surface-border hover:bg-surface/30 transition-colors ${log.status === "error" ? "bg-red-500/[0.02]" : log.status === "fallback" ? "bg-amber-500/[0.02]" : ""}`}>
                      <td className="px-4 py-2.5 text-text-muted font-mono text-[10px] whitespace-nowrap">{formatTime(log.created_at)}</td>
                      <td className="px-4 py-2.5"><span className="text-text-primary text-xs font-medium whitespace-nowrap">{log.agent_name}</span><span className="text-text-muted text-[10px] font-mono ml-1">{log.agent_id}</span></td>
                      <td className="px-4 py-2.5 text-text-secondary text-[11px] font-mono">{log.role_key}</td>
                      <td className="px-4 py-2.5"><span className="text-text-primary text-xs">{log.provider}</span><span className="text-text-muted text-[10px] font-mono ml-1">{log.model_id}</span></td>
                      <td className="px-4 py-2.5"><span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-surface text-text-secondary font-mono">{log.request_type}</span></td>
                      <td className="px-4 py-2.5 text-right text-text-primary font-mono text-xs">{log.prompt_tokens.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-text-primary font-mono text-xs">{log.completion_tokens.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right text-text-primary font-mono text-xs font-medium">{log.total_tokens.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right"><span className={`font-mono text-xs ${log.latency_ms > 120 ? "text-red-500" : log.latency_ms > 60 ? "text-amber-500" : log.latency_ms > 0 ? "text-emerald-500" : "text-text-muted"}`}>{log.latency_ms > 0 ? `${log.latency_ms}ms` : "--"}</span></td>
                      <td className="px-4 py-2.5 text-right text-text-primary font-mono text-xs">{log.cost.toFixed(4)}</td>
                      <td className="px-4 py-2.5 text-center"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${st.cls}`}>{st.label}</span></td>
                      <td className="px-4 py-2.5 text-center"><Link to={`/forex/model-logs/detail?id=${log.log_id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand whitespace-nowrap"><i className="ri-eye-line text-xs" />详情</Link></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
